import { useId, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useT } from '../../i18n';
import { eraseMyAccount } from '../../lib/api/personalData';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ═══════════════════════════════════════════════════════════════════
// «SLETT KONTOEN MIN OG DATAENE MINE» — THE PERSON ERASES THEIR DATA (#1062).
//
// `POST /me/erase` closes the account and erases StarPoint's own records about
// the person in one transaction (PERSONAL_DATA.md § 5.2). It is the one act in
// the product that cannot be undone, so the confirmation is the strongest the
// app has:
//
//   • THE SHARED `Dialog`, never `window.confirm()` (DOMAIN_RULES) — named,
//     modal, Escape is «Avbryt», and focus starts on «Avbryt»;
//   • IT SAYS WHAT GOES AND WHAT STAYS. The results and the rating history stay,
//     without a name, because other players' ratings were computed from them;
//     each club's own records — bookings and payments, the bookkeeping — stay
//     with the club. Somebody who reads «slett» as «everything, everywhere» must
//     not be misled (§ 5.6);
//   • A TYPED WORD. The button stays disabled until the person has typed the
//     confirmation word, which comes from the catalog so it is a word in the
//     language they read.
//
// A FRESH SIGN-IN: the server wants one from the last fifteen minutes; an older
// one is answered `reauthentication_required`, the transport opens the step-up
// prompt, and the person presses again. AFTERWARDS THE SESSION IS GONE — the
// server revoked it with the account — and «Ferdig» takes the person to the
// signed-out screen through the host's own sign-out path.
// ═══════════════════════════════════════════════════════════════════

export interface EraseAccountSectionProps {
  /**
   * Leave the signed-in surface once the account is erased — the app's own
   * sign-out path. Absent where the host has none; the done state stands alone.
   */
  onErased?: (() => void) | null;
  /** Injectable for tests. */
  erase?: typeof eraseMyAccount;
}

type State =
  | { kind: 'idle' }
  | { kind: 'asking' }
  | { kind: 'working' }
  | { kind: 'done' }
  | { kind: 'failed'; code: string };

/** The typed word matches when it is the catalog's, ignoring case and spaces around it. */
export function confirmationMatches(typed: string, word: string): boolean {
  return typed.trim().toLocaleUpperCase() === word.trim().toLocaleUpperCase() && word.trim() !== '';
}

export function EraseAccountSection({
  onErased = null,
  erase = eraseMyAccount,
}: EraseAccountSectionProps) {
  const t = useT();
  const uid = safeId('erase-account', useId());
  const titleId = `${uid}-title`;
  const bodyId = `${uid}-body`;
  const inputId = `${uid}-word`;
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [typed, setTyped] = useState('');
  const word = t('account.erase.word');
  const armed = confirmationMatches(typed, word);

  const failure = (code: string): string => {
    if (code === 'reauthentication_required') return t('account.erase.reauth');
    if (code === 'last_role_manager') return t('account.erase.lastRoleManager');
    if (code === 'unreachable') return t('account.erase.unreachable');
    return t('account.erase.failed');
  };

  const confirm = async () => {
    if (!armed) return;
    setState({ kind: 'working' });
    const res = await erase();
    setState(res.code === null ? { kind: 'done' } : { kind: 'failed', code: res.code });
  };

  const close = () => {
    if (state.kind === 'working') return;
    if (state.kind === 'done') {
      onErased?.();
      return;
    }
    setState({ kind: 'idle' });
    setTyped('');
  };

  const open = state.kind !== 'idle';

  return (
    <section className="card p-5" aria-labelledby={titleId} data-testid="erase-account">
      <h2
        id={titleId}
        className="font-display text-lg font-bold text-ink inline-flex items-center gap-2"
      >
        <Trash2 size={18} aria-hidden="true" /> {t('account.erase.title')}
      </h2>
      <p className="mt-1 text-sm text-ink-2">{t('account.erase.lead')}</p>
      <div className="mt-3">
        <button
          type="button"
          className="btn-outline"
          onClick={() => setState({ kind: 'asking' })}
          data-testid="erase-account-start"
        >
          {t('account.erase.start')}
        </button>
      </div>

      {open && (
        <Dialog
          heading={t('account.erase.dialogTitle')}
          describedBy={bodyId}
          onClose={close}
          closeOnBackdropClick={state.kind !== 'working'}
        >
          <div data-testid="erase-account-dialog" className="flex flex-col gap-3 text-sm">
            {state.kind === 'done' ? (
              <p id={bodyId} role="status" className="text-ink" data-testid="erase-account-done">
                {t('account.erase.done')}
              </p>
            ) : (
              <div id={bodyId} className="flex flex-col gap-3">
                <div>
                  <h3 className="font-semibold text-ink">{t('account.erase.deletedHeading')}</h3>
                  <ul className="mt-1 list-disc pl-5 text-ink-2 space-y-0.5">
                    <li>{t('account.erase.deleted1')}</li>
                    <li>{t('account.erase.deleted2')}</li>
                    <li>{t('account.erase.deleted3')}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-ink">{t('account.erase.keptHeading')}</h3>
                  <ul className="mt-1 list-disc pl-5 text-ink-2 space-y-0.5">
                    <li>{t('account.erase.kept1')}</li>
                    <li>{t('account.erase.kept2')}</li>
                    <li>{t('account.erase.kept3')}</li>
                  </ul>
                </div>
                <p className="text-ink-2">{t('account.erase.newAccount')}</p>
                <p className="text-xs text-ink-3">{t('account.erase.exportFirst')}</p>
              </div>
            )}

            {state.kind !== 'done' && (
              <div className="flex flex-col gap-1">
                <label htmlFor={inputId} className="font-medium text-ink">
                  {t('account.erase.typeLabel', { word })}
                </label>
                <input
                  id={inputId}
                  className="input"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  value={typed}
                  disabled={state.kind === 'working'}
                  onChange={(e) => setTyped(e.target.value)}
                  data-testid="erase-account-word"
                />
              </div>
            )}

            {state.kind === 'failed' && (
              <p className="text-rose-700" role="alert" data-testid="erase-account-error">
                {failure(state.code)}
              </p>
            )}

            <div className="flex flex-wrap gap-2 justify-end">
              {state.kind === 'done' ? (
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => onErased?.()}
                  data-testid="erase-account-continue"
                >
                  {t('account.erase.continue')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={state.kind === 'working'}
                    onClick={close}
                  >
                    {t('account.erase.cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    disabled={!armed || state.kind === 'working'}
                    onClick={() => void confirm()}
                    data-testid="erase-account-confirm"
                  >
                    {state.kind === 'working'
                      ? t('account.erase.working')
                      : t('account.erase.confirm')}
                  </button>
                </>
              )}
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
}
